import { supabaseClient } from "@/lib/supabase";
import {
	type UseSuspenseQueryResult,
	queryOptions,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";

import type { ExtendedProfileT } from "../../../types/database/profiles";
import { useAuth } from "../../../utils/auth";

export async function getProfile(id: string): Promise<ExtendedProfileT | null> {
	if (!id) {
		throw new Error("No user id provided");
	}

	if (!validateUUID(id)) {
		throw new Error("Invalid user id format");
	}

	const { data, error } = await supabaseClient
		.from("profiles")
		.select("*, users_organisations_roles_view(*)")
		.eq("user_id", id)
		.returns<ExtendedProfileT>()
		.single();

	if (error) {
		if (error.code === "PGRST116") {
			throw new Error(`Profile not found for user ${id}`);
		}
		throw error;
	}

	if (!data) {
		return null;
	}

	return data;
}

export const createProfileQueryKey = (id: string) => [
	"profile",
	{ user_id: id },
];

export const selectProfileByIdQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: createProfileQueryKey(userId),
		queryFn: () => getProfile(userId),
		enabled: Boolean(userId && validateUUID(userId)),
		retry: true,
		retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 10000),
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
		staleTime: 0,
	});
};

export function useProfileQuery(): UseSuspenseQueryResult<
	ExtendedProfileT,
	Error
> {
	const auth = useAuth();
	const profileQuery = useSuspenseQuery(selectProfileByIdQueryOptions(auth.id));
	auth.setIsSuperUser(profileQuery?.data?.is_super_user ?? false);
	return profileQuery;
}

export function useProfileByIdQuery(userId: string) {
	const profileQuery = useSuspenseQuery(selectProfileByIdQueryOptions(userId));
	return profileQuery;
}
